import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Plus as PlusIcon,
  X as XIcon,
  Upload as UploadIcon,
  FileText as FileTextIcon,
  Target as TargetIcon,
  CheckCircle as CheckCircleIcon,
  Briefcase as BriefcaseIcon,
} from 'lucide-react';
import { getProfile, saveProfile, uploadResume, regenerateTitles, getApiErrorMessage } from '../services/api';
import { designSystem } from '../theme';

const EMPTY_PROFILE = {
  name: '',
  title: '',
  location: '',
  email: '',
  phone: '',
  linkedin: '',
  desired_titles: [],
  desired_locations: [],
  min_salary: '',
  max_salary: '',
  remote_preference: 'any',
  job_type: 'any',
  skills: [],
  tasks: [],
  industries: [],
  interests: [],
  summary: ''
};

function Profile({ role = 'seeker' }) {
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  const [regenerating, setRegenerating] = useState(false);
  
  const [newSkill, setNewSkill] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newIndustry, setNewIndustry] = useState('');
  const [newInterest, setNewInterest] = useState('');
  const [newTask, setNewTask] = useState({ name: '', hours: '', years: '', notes: '' });

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      await loadProfile(cancelled);
    };
    init();

    return () => { cancelled = true; };
  }, []);

  const loadProfile = async (cancelled = false) => {
    try {
      setLoading(true);
      const data = await getProfile();
      if (cancelled) return;
      if (data) {
        setProfile({
          ...EMPTY_PROFILE,
          ...data,
          min_salary: data.min_salary || '',
          max_salary: data.max_salary || '',
        });
      }
    } catch (err) {
      if (!cancelled) console.error('Failed to load profile:', err);
    } finally {
      if (!cancelled) setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      const profileToSave = {
        ...profile,
        min_salary: profile.min_salary ? parseInt(profile.min_salary) : null,
        max_salary: profile.max_salary ? parseInt(profile.max_salary) : null,
      };
      
      await saveProfile(profileToSave);
      setMessage({ type: 'success', text: 'Profile saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save profile.' });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const addToArray = (field, value, setter) => {
    if (value.trim()) {
      setProfile(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
      setter('');
    }
  };

  const removeFromArray = (field, index) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const addTask = () => {
    if (newTask.name.trim()) {
      const task = {
        name: newTask.name.trim(),
        hours: newTask.hours ? parseInt(newTask.hours) : null,
        years: newTask.years ? parseFloat(newTask.years) : null,
        notes: newTask.notes || null
      };
      setProfile(prev => ({
        ...prev,
        tasks: [...(prev.tasks || []), task]
      }));
      setNewTask({ name: '', hours: '', years: '', notes: '' });
    }
  };

  const removeTask = (index) => {
    setProfile(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== index)
    }));
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);
    setMessage(null);

    try {
      const result = await uploadResume(file);
      setUploadResult(result);
      setMessage({ type: 'success', text: `Resume parsed! Added ${result.skills_added} skills and ${result.tasks_added} tasks.` });
      await loadProfile();
    } catch (err) {
      const errorDetail = getApiErrorMessage(err, 'Failed to parse resume');
      setMessage({ type: 'error', text: errorDetail });
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleRegenerateTitles = async () => {
    setRegenerating(true);
    setMessage(null);
    
    try {
      const result = await regenerateTitles();
      setMessage({ type: 'success', text: `Generated ${result.suggested_titles?.suggested_titles?.length || 0} AI-suggested titles!` });
      await loadProfile();
    } catch (err) {
      const errorDetail = getApiErrorMessage(err, 'Failed to generate titles');
      setMessage({ type: 'error', text: errorDetail });
      console.error(err);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      {/* Page Header */}
      <Typography variant="h4" fontWeight="600" sx={{ mb: 1, pt: 1 }}>
        {role === 'provider' ? 'Employer Admin Profile' : 'Job Seeker Profile'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {role === 'provider' 
          ? 'Your contact information as the hiring manager — this is how candidates reach you' 
          : 'Your resume, skills, and job search preferences'}
      </Typography>

      {/* Save Button */}
      <Box 
        sx={{
          mb: 3,
        }}
      >
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={saving}
            sx={{ ...designSystem.button.contained, py: 1.5, fontSize: '1rem', fontWeight: 'bold' }}
          >
            {saving ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save Profile'}
          </Button>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Resume Upload — Seeker only */}
      {role === 'seeker' && (
        <Card variant="light" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <FileTextIcon size={24} color="#1e88e5" />
              <Typography variant="h6" fontWeight="600" sx={{ color: 'text.primary' }}>
                Upload Resume
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              Upload your resume (PDF, DOCX, or TXT) to auto-populate your profile
            </Typography>
            <Box
              component="input"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              sx={{ display: 'none' }}
              id="resume-upload-input"
              type="file"
              onChange={handleResumeUpload}
              disabled={uploading}
            />
            <label htmlFor="resume-upload-input">
              <Button
                component="span"
                variant="outlined"
                size="large"
                startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon size={20} />}
                disabled={uploading}
              >
                {uploading ? 'Parsing Resume...' : 'Choose File'}
              </Button>
            </label>
            {uploadResult && (
              <Box mt={2}>
                <Typography variant="subtitle2" fontWeight="bold">Extracted:</Typography>
                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                  {uploadResult.extracted.skills.length > 0 && (
                    <Chip 
                      icon={<TargetIcon size={16} />}
                      label={`${uploadResult.extracted.skills.length} skills`} 
                      size="small" 
                      color="primary" 
                    />
                  )}
                  {uploadResult.extracted.tasks.length > 0 && (
                    <Chip 
                      icon={<CheckCircleIcon size={16} />}
                      label={`${uploadResult.extracted.tasks.length} tasks`} 
                      size="small" 
                      color="primary" 
                    />
                  )}
                  {uploadResult.extracted.title && (
                    <Chip 
                      icon={<BriefcaseIcon size={16} />}
                      label={uploadResult.extracted.title} 
                      size="small" 
                      color="primary" 
                    />
                  )}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Basic Information — All roles */}
      <Card variant="light" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 3, color: 'text.primary' }}>
            {role === 'provider' ? 'Business Contact' : 'Basic Information'}
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={role === 'provider' ? 'Your Name *' : 'Full Name *'}
                value={profile.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={role === 'provider' ? 'Jane Smith' : 'Your Name'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={role === 'provider' ? 'Your Role / Title' : 'Current Title'}
                value={profile.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder={role === 'provider' ? 'Owner, Hiring Manager, HR Director' : 'Lead Software Engineer'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={role === 'provider' ? 'Business Location' : 'Location'}
                value={profile.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder={role === 'provider' ? 'Brooklyn, NY' : 'Manhattan, NY'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label={role === 'provider' ? 'Work Email' : 'Email'}
                value={profile.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder={role === 'provider' ? 'hiring@yourcompany.com' : 'gerald@example.com'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="tel"
                label={role === 'provider' ? 'Business Phone' : 'Phone'}
                value={profile.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="url"
                label="LinkedIn URL"
                value={profile.linkedin}
                onChange={(e) => handleChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Job Preferences — Seeker only */}
      {role === 'seeker' && (
        <Card variant="light" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 3, color: 'text.primary' }}>
              Job Preferences
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Salary ($/year)"
                  value={profile.min_salary}
                  onChange={(e) => handleChange('min_salary', e.target.value)}
                  placeholder="150000"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Salary ($/year)"
                  value={profile.max_salary}
                  onChange={(e) => handleChange('max_salary', e.target.value)}
                  placeholder="250000"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Remote Preference</InputLabel>
                  <Select
                    value={profile.remote_preference}
                    onChange={(e) => handleChange('remote_preference', e.target.value)}
                    label="Remote Preference"
                  >
                    <MenuItem value="any">Any</MenuItem>
                    <MenuItem value="remote">Remote Only</MenuItem>
                    <MenuItem value="hybrid">Hybrid</MenuItem>
                    <MenuItem value="onsite">Onsite Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Job Type</InputLabel>
                  <Select
                    value={profile.job_type}
                    onChange={(e) => handleChange('job_type', e.target.value)}
                    label="Job Type"
                  >
                    <MenuItem value="any">Any</MenuItem>
                    <MenuItem value="full_time">Full-Time</MenuItem>
                    <MenuItem value="part_time">Part-Time</MenuItem>
                    <MenuItem value="contract">Contract</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'text.primary' }}>
                AI-Suggested Titles
              </Typography>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={handleRegenerateTitles}
                disabled={regenerating}
                startIcon={regenerating ? <CircularProgress size={16} /> : <TargetIcon size={16} />}
              >
                {regenerating ? 'Generating...' : 'Regenerate'}
              </Button>
            </Box>
            {profile.suggested_titles && Array.isArray(profile.suggested_titles) && profile.suggested_titles.length > 0 ? (
              <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                {profile.suggested_titles.map((title, i) => (
                  <Chip
                    key={i}
                    label={title}
                    color="primary"
                    variant="outlined"
                    icon={<TargetIcon size={14} />}
                  />
                ))}
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>
                Upload a resume or click "Regenerate" to get AI-powered job title suggestions based on your profile.
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight="600" gutterBottom sx={{ color: 'text.primary' }}>
              Custom Job Titles
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Add your own custom titles to search for
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                fullWidth
                size="small"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Add a title..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('desired_titles', newTitle, setNewTitle))}
              />
                  <IconButton color="primary" onClick={() => addToArray('desired_titles', newTitle, setNewTitle)}>
                    <PlusIcon size={20} />
                  </IconButton>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {profile.desired_titles?.map((title, i) => (
                <Chip
                  key={i}
                  label={title}
                  onDelete={() => removeFromArray('desired_titles', i)}
                />
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight="600" gutterBottom sx={{ color: 'text.primary' }}>
              Desired Locations
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                fullWidth
                size="small"
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                placeholder="Add a location..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('desired_locations', newLocation, setNewLocation))}
              />
                  <IconButton color="primary" onClick={() => addToArray('desired_locations', newLocation, setNewLocation)}>
                    <PlusIcon size={20} />
                  </IconButton>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {profile.desired_locations?.map((loc, i) => (
                <Chip
                  key={i}
                  label={loc}
                  onDelete={() => removeFromArray('desired_locations', i)}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Skills — Seeker only */}
      {role === 'seeker' && (
        <Card variant="light" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: 'text.primary' }}>
              Skills
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Technical and professional skills
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                fullWidth
                size="small"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="Add a skill (e.g., Python, Financial Modeling, Team Leadership)..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('skills', newSkill, setNewSkill))}
              />
              <IconButton color="primary" onClick={() => addToArray('skills', newSkill, setNewSkill)}>
                <PlusIcon size={20} />
              </IconButton>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {profile.skills?.map((skill, i) => (
                <Chip
                  key={i}
                  label={skill}
                  color="primary"
                  variant="outlined"
                  onDelete={() => removeFromArray('skills', i)}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tasks & Experience — Seeker only */}
      {role === 'seeker' && (
        <Card variant="light" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 1, color: 'text.primary' }}>
              Tasks & Experience
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              What have you actually done? How long did you do it?
            </Typography>
            
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  value={newTask.name}
                  onChange={(e) => setNewTask(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Task (e.g., build market data systems)"
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={newTask.hours}
                  onChange={(e) => setNewTask(prev => ({ ...prev, hours: e.target.value }))}
                  placeholder="Hours"
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={newTask.years}
                  onChange={(e) => setNewTask(prev => ({ ...prev, years: e.target.value }))}
                  placeholder="Years"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button fullWidth variant="outlined" onClick={addTask}>
                  Add
                </Button>
              </Grid>
            </Grid>

            {profile.tasks?.map((task, i) => (
              <Card key={i} variant="outlined" sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {task.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.hours && `${task.hours.toLocaleString()} hrs`}
                        {task.hours && task.years && ' • '}
                        {task.years && `${task.years} yrs`}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => removeTask(i)}>
                      <XIcon size={16} />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Industries & Interests — Seeker only */}
      {role === 'seeker' && (
        <Card variant="light" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 3, color: 'text.primary' }}>
              Industries & Interests
            </Typography>
            
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
              Industries
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                fullWidth
                size="small"
                value={newIndustry}
                onChange={(e) => setNewIndustry(e.target.value)}
                placeholder="Add industry..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('industries', newIndustry, setNewIndustry))}
              />
              <IconButton color="primary" onClick={() => addToArray('industries', newIndustry, setNewIndustry)}>
                <PlusIcon size={20} />
              </IconButton>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
              {profile.industries?.map((ind, i) => (
                <Chip
                  key={i}
                  label={ind}
                  onDelete={() => removeFromArray('industries', i)}
                />
              ))}
            </Box>

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Interests
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                fullWidth
                size="small"
                value={newInterest}
                onChange={(e) => setNewInterest(e.target.value)}
                placeholder="Add interest..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('interests', newInterest, setNewInterest))}
              />
              <IconButton color="primary" onClick={() => addToArray('interests', newInterest, setNewInterest)}>
                <PlusIcon size={20} />
              </IconButton>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {profile.interests?.map((int, i) => (
                <Chip
                  key={i}
                  label={int}
                  onDelete={() => removeFromArray('interests', i)}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Summary — All roles */}
      <Card variant="light" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: 'text.primary' }}>
            {role === 'provider' ? 'About You & Your Hiring Needs' : 'Summary'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {role === 'provider'
              ? 'Candidates see this when they view your profile — tell them who you are, what your business does, and what kind of people you\'re looking to hire.'
              : 'Brief summary of your background and what you\'re looking for'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={5}
            value={profile.summary}
            onChange={(e) => handleChange('summary', e.target.value)}
            placeholder={
              role === 'provider' 
                ? 'I run a restaurant group in Brooklyn and I\'m always looking for reliable kitchen staff and front-of-house managers...' 
                : 'Brief summary of your background and what you\'re looking for...'
            }
          />
        </CardContent>
      </Card>

    </Container>
  );
}

export default Profile;
